import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  throwError,
  of,
  Subject,
  interval,
  combineLatest
} from 'rxjs';
import { tap, switchMap, map, catchError, shareReplay, filter } from 'rxjs/operators';
import {
  AuthResultResponse,
  Client,
  JWT,
  UserMeResponse,
  User,
  PlatformUrlName,
  ClientSubscriptionResponse,
  DemoRequestBody,
  FacebookAuth,
  ClientInfo
} from '@fb-tool-front/shared/interfaces/auth.type';
import { CookieService } from 'ngx-cookie-service';
import { JwtHelperService } from '@auth0/angular-jwt';
import dayjs from 'dayjs';
import { tuiPure } from '@taiga-ui/cdk';
import {
  ClientSubscriptionTypes,
  MISSING_REFRESH_TOKEN_ERROR
} from '@fb-tool-front/shared/constants/auth.constants';
import { environment } from '@fb-tool-front/environments/environment';
import { cookiePrefix, localStorageUtm } from '@fb-tool-front/shared/constants/shared.constant';
import { HISTORY, LOCAL_STORAGE, LOCATION } from '@ng-web-apis/common';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly COOKIES_CHECKING_INTERVAL = 4000;
  private resetCode$$ = new BehaviorSubject<string>(null);
  public resetCode$ = this.resetCode$$.asObservable();
  private facebookAuthData$$ = new BehaviorSubject<FacebookAuth>(null);
  public facebookAuthData$ = this.facebookAuthData$$.asObservable();
  public temporaryUser = '';
  public temporaryCodeError = '';
  public temporaryCode = '';

  public redirectToVendor$$ = new Subject<boolean>();
  public redirectToVendor$ = this.redirectToVendor$$.asObservable();
  public user$$ = new BehaviorSubject<User>(null);
  public user$ = this.user$$.asObservable();
  private client$$ = new BehaviorSubject<Client>(null);
  public client$ = this.client$$.asObservable();
  private clientsError$$ = new BehaviorSubject<boolean>(false);
  public clientsError$ = this.clientsError$$.asObservable();
  private allClientsList$$ = new BehaviorSubject<Client[]>(null);
  public allClientsList$ = this.allClientsList$$.asObservable();
  public isStatisticsShown$ = combineLatest([this.user$, this.client$]).pipe(
    map(
      ([user, client]: [User, Client]) =>
        user &&
        (user.impersonated || this.isClientSubscription(ClientSubscriptionTypes.facebookStatistics))
    )
  );
  private initClientsObservable$: Observable<Client[]>;

  private helperJwt = new JwtHelperService();
  private authUrl = '';
  private subscriptionUrl = '';
  private user: User;
  private clientIdFromUrl = '';
  private switchedUser = '';
  private TOKENS;
  private cookieDomain = `.${environment.aitarget_domain || 'aitarget.com'}`;
  private cookiePrefix = cookiePrefix || 'ai_';
  private countryCode;

  public userData$$ = new BehaviorSubject<User['data']>(null);
  public userData$ = this.userData$$.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
    private cookieService: CookieService,
    private zone: NgZone,
    private title: Title,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
    @Inject(LOCATION) readonly location: Location,
    @Inject(HISTORY) readonly history: History
  ) {
    this.setClientsObservable();
    this.TOKENS = this.setTokens();
    this.authUrl = `${environment.authHost}/api/auth`;
    this.subscriptionUrl = `${environment.authHost}/api/subscription/request`;

    const facebookCodeAndStatusRegExp = /[&]*(login|signup)\?code=([^&#^=]*)&state=([^&#^=]*)/;
    const facebookCodeAndStatusFromUrl = facebookCodeAndStatusRegExp.exec(this.location.href);

    if (facebookCodeAndStatusFromUrl) {
      this.logout(false);
      this.facebookAuthData$$.next({
        code: facebookCodeAndStatusFromUrl[2],
        state: facebookCodeAndStatusFromUrl[3]
      });
    } else {
      this.facebookAuthData$$.next({ code: null, state: null });
    }

    const resetCodeRegExp = /[&]*reset\?code=([^&#^=]*)/;
    const resetCodeFromUrl = resetCodeRegExp.exec(this.location.href);

    if (resetCodeFromUrl) {
      this.resetCode$$.next(resetCodeFromUrl[1]);
      this.history.replaceState(null, this.title.getTitle(), this.location.href.split('?')[0]);
      this.logout(false);
    } else {
      this.resetCode$$.next('');
    }

    const confirmCodeRegExp = /[&]*confirm\?code=([^&#^=]*)/;
    const confirmCodeFromUrl = confirmCodeRegExp.exec(this.location.href);

    if (confirmCodeFromUrl && !resetCodeFromUrl) {
      this.temporaryCode = confirmCodeFromUrl[1];
      this.history.replaceState(null, this.title.getTitle(), this.location.href.split('?')[0]);
      this.logout(false);
    }

    const utmCodesRegExp = /(utm_.*?)\=(.+?(?=(&|$)))/gim;
    const utmCodesFromUrl = this.location.href?.match(utmCodesRegExp);

    if (utmCodesFromUrl) {
      this.localStorage.setItem(
        localStorageUtm,
        JSON.stringify(
          utmCodesFromUrl.reduce((resultObject, el) => {
            const newLine = el.split('=');

            return { ...resultObject, [newLine[0]]: newLine[1] };
          }, {})
        )
      );

      this.history.replaceState(null, this.title.getTitle(), this.location.href.split('?')[0]);
    }

    const accessToken = this.cookieService.get(this.TOKENS.main);

    if (accessToken && !confirmCodeFromUrl && !resetCodeFromUrl) {
      const decodedAccessToken: JWT = this.decodeToken(accessToken);
      if (!decodedAccessToken) {
        return;
      }

      this.user = {
        id: decodedAccessToken.id,
        username: decodedAccessToken.username,
        roles: decodedAccessToken.roles,
        email: this.getEmail(decodedAccessToken.email),
        isAitarget: decodedAccessToken.roles.includes('ROLE_AITARGET')
      };

      const switchUserNameRegExp = /[&]*x-switch-user=([^&#^=]*)/;
      const switchUserNameFromUrl = switchUserNameRegExp.exec(this.location.href);

      if (switchUserNameFromUrl) {
        this.cookieService.set(
          this.TOKENS.switchedUser,
          switchUserNameFromUrl[1],
          dayjs().add(1, 'day').toDate(),
          '/',
          this.cookieDomain
        );

        this.history.replaceState(null, this.title.getTitle(), this.location.href.split('?')[0]);
      }

      const urlParams = new URLSearchParams(this.location.search);
      this.clientIdFromUrl = urlParams.get('clientId');

      if (this.clientIdFromUrl) {
        this.history.replaceState(null, this.title.getTitle(), this.location.href.split('?')[0]);
      }

      const switchUserName = this.cookieService.get(this.TOKENS.switchedUser);
      this.switchedUser = switchUserName;

      if (switchUserName) {
        this.user = {
          ...this.user,
          impersonated: true,
          username: switchUserName,
          email: this.getEmail(decodedAccessToken.email)
        };
      }

      this.user$$.next(this.user);
    }

    this.zone.runOutsideAngular(() => {
      interval(this.COOKIES_CHECKING_INTERVAL).subscribe(_ => {
        const hasRefreshOrMainToken =
          !!this.cookieService.get(this.TOKENS.main) ||
          !!this.cookieService.get(this.TOKENS.refresh);

        if (this.user && !hasRefreshOrMainToken) {
          this.zone.run(() => this.logout());
        }

        if (!this.user && hasRefreshOrMainToken) {
          this.location.href = '';
        }

        if (this.user && this.cookieService.get(this.TOKENS.switchedUser) !== this.switchedUser) {
          this.location.href = '';
        }
      });
    });
  }

  @tuiPure
  public getToolUrl(value: PlatformUrlName) {
    return environment.production
      ? `https://${value}-tool${this.cookieDomain}`
      : `https://${value}-tool-front-${environment.branchName || 'master'}${this.cookieDomain}`;
  }

  @tuiPure
  public get mainUrl() {
    return `https://www${this.cookieDomain}`;
  }

  private getEmail(email: string) {
    return email?.replace('@example.com', '');
  }

  public changeUserData(data: User['data']): Observable<User['data']> {
    if (!this.user) {
      return of(null);
    }

    return this.changeUserDataRequest(data).pipe(
      tap(() => this.userData$$.next({ ...this.userData$$.value, ...data }))
    );
  }

  private setClientsObservable() {
    const setUserData = (userMeRespose: UserMeResponse): void => {
      this.userData$$.next(userMeRespose.data || []);
    };

    const getClientsFromResponse = (userMeRespose: UserMeResponse): Client[] => {
      return userMeRespose.clients;
    };

    const setAllClientsList = (clients: Client[]): void => {
      const sortCallback = (a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      };

      this.allClientsList$$.next(clients.sort(sortCallback));
    };

    const setDefaultClient = (clients: Client[]): void => {
      const clientId = this.clientIdFromUrl || this.cookieService.get(this.TOKENS.client);

      const foundedClient = clientId
        ? clients.find(el => el.id === clientId)
        : clients.length === 1
        ? clients[0]
        : null;

      if (foundedClient) {
        this.client$$.next(foundedClient);
      }
    };

    this.initClientsObservable$ = this.http
      .get<UserMeResponse>(`${environment.authHost}/api/user/me`, {
        headers: new HttpHeaders({
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Cache-Control': 'no-cache',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Pragma: 'no-cache',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT'
        })
      })
      .pipe(
        catchError(error => {
          console.error(error);
          // workaround to prevent circular DI error
          // this.errorService.captureError(error);

          this.clientsError$$.next(true);
          return of({ clients: [] });
        }),
        tap(setUserData),
        map(getClientsFromResponse),
        tap(setAllClientsList),
        tap(setDefaultClient),
        shareReplay(1)
      );
  }

  private initClients() {
    return this.initClientsObservable$;
  }

  private setTokens() {
    return {
      main: environment.production
        ? `${this.cookiePrefix}PROD_share_token`
        : `${this.cookiePrefix}STAGE_share_token`,
      client: environment.production
        ? `${this.cookiePrefix}PROD_share_client_id`
        : `${this.cookiePrefix}STAGE_share_client_id`,
      refresh: environment.production
        ? `${this.cookiePrefix}PROD_share_refresh_token`
        : `${this.cookiePrefix}STAGE_share_refresh_token`,
      switchedUser: environment.production
        ? `${this.cookiePrefix}PROD_share_switch_user`
        : `${this.cookiePrefix}STAGE_share_switch_user`
    };
  }

  private decodeToken(token) {
    let result;

    try {
      result = this.helperJwt.decodeToken(token);
    } catch (e) {
      console.error(e);
      // workaround to prevent circular DI error
      // this.errorService.captureError(e);

      this.logout();
    }

    return result;
  }

  public getCurrentClient(): Client {
    return this.client$$.getValue();
  }

  public isClientSubscription(value: ClientSubscriptionTypes): boolean {
    return this.client$$.getValue()?.subscriptions.some(el => el.id === value);
  }

  public getSwitchedUser(): string {
    return this.switchedUser;
  }

  public getClientSubscription(subscription: string) {
    return this.http.get<ClientSubscriptionResponse>(`${this.subscriptionUrl}`, {
      params: {
        client: this.getCurrentClient().id,
        subscription
      }
    });
  }

  public getClientInfo$(client: string) {
    return this.http.get<ClientInfo>(`${environment.host}/api/client/info`, {
      params: {
        client
      }
    });
  }

  public setClientSubscription({
    subscription,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    contact_name,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    contact_email,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    contact_phone,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    contact_company,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    prefer_whats_app
  }: DemoRequestBody) {
    return this.http.post<ClientSubscriptionResponse>(`${this.subscriptionUrl}`, {
      client: this.getCurrentClient().id,
      subscription,
      contact_name,
      contact_email,
      contact_phone,
      contact_company,
      prefer_whats_app
    });
  }

  public changePassword(password: string) {
    return this.http
      .post<any>(`${this.authUrl}/change_password`, {
        code: this.resetCode$$.value,
        password
      })
      .pipe(tap((authResult: AuthResultResponse) => this.setSession(authResult)));
  }

  public resetPassword(username: string, recaptcha: string) {
    return this.http.post<any>(`${this.authUrl}/reset_password`, {
      username,
      'g-recaptcha-response': recaptcha
    });
  }

  public getUtm(): string {
    try {
      const utm = JSON.parse(this.localStorage.getItem(localStorageUtm));

      return utm || undefined;
    } catch (e) {}
  }

  public register(
    username: string,
    password: string,
    data: User['data']
    // recaptcha: string
  ) {
    return this.http.post<any>(`${this.authUrl}/register`, {
      username,
      password,
      data
      // 'g-recaptcha-response': recaptcha
    });
  }

  public quickRegister(username: string, data: User['data']) {
    return this.http
      .post<any>(`${this.authUrl}/quick_register`, {
        username,
        data
      })
      .pipe(tap((authResult: AuthResultResponse) => this.setSession(authResult)));
  }

  public resend(username: string) {
    return this.http.post<any>(`${this.authUrl}/resend`, {
      username
    });
  }

  public confirm(code: string) {
    return this.http
      .post<any>(`${this.authUrl}/confirm`, {
        code
      })
      .pipe(
        tap(authResult => {
          this.setSession(authResult);
        })
      );
  }

  public loginWithFacebook() {
    return this.http.post<any>(`${this.authUrl}/facebook/login`, {
      redirect_url: this.location.href
    });
  }

  public checkLoginWithFacebook(body: FacebookAuth): Observable<AuthResultResponse> {
    return this.http
      .post<AuthResultResponse>(`${this.authUrl}/facebook/check`, body)
      .pipe(tap((authResult: AuthResultResponse) => this.setSession(authResult)));
  }

  public login(username: string, password: string, captcha: string) {
    return this.http
      .post<AuthResultResponse>(`${this.authUrl}/login`, {
        username,
        password,
        'g-recaptcha-response': captcha
      })
      .pipe(tap((authResult: AuthResultResponse) => this.setSession(authResult)));
  }

  public logout(redirectToLogin = true) {
    this.allClientsList$$.next(null);
    this.setClientsObservable(); // need new observalbe because of shareReplay returns clients from last user
    this.cookieService.delete(this.TOKENS.switchedUser, '/', this.cookieDomain);
    this.cookieService.delete(this.TOKENS.switchedClientsList, '/', this.cookieDomain);
    this.redirectToVendor$$.next(true);

    if (this.user && this.user.impersonated) {
      this.location.reload();
    } else {
      this.cookieService.delete(this.TOKENS.main, '/', this.cookieDomain);
      this.cookieService.delete(this.TOKENS.refresh, '/', this.cookieDomain);
      this.cookieService.delete(this.TOKENS.client, '/', this.cookieDomain);
      this.user = null;
      this.user$$.next(null);
      this.userData$$.next(null);
      this.client$$.next(null);
      if (redirectToLogin) {
        this.router.navigate(['login']);
      }
    }
  }

  public setClient(clientId: string) {
    const client = this.allClientsList$$.getValue().find(el => el.id === clientId);

    this.cookieService.set(
      this.TOKENS.client,
      client.id,
      dayjs().add(30, 'day').toDate(),
      '/',
      this.cookieDomain
    );
    this.client$$.next(client);
  }

  public changeClient() {
    this.redirectToVendor$$.next(true);
    this.cookieService.delete(this.TOKENS.client, '/', this.cookieDomain);
    this.client$$.next(null);

    this.router.navigate(['client']);
  }

  public isLoggedInAndInitClients(): Observable<boolean> {
    if (!this.TOKENS) {
      throw new Error(`Tokens not defined. Check forRoot method's params`);
    }

    return of(!this.isTokenExpired()).pipe(
      switchMap(res =>
        res
          ? of(true)
          : this.refreshToken().pipe(
              map(token => !!token),
              catchError(() => of(false))
            )
      ),
      switchMap(res => {
        if (res) {
          return this.initClients().pipe(
            filter(clients => !!clients),
            map(_ => true)
          );
        } else {
          return of(false);
        }
      })
    );
  }

  public refreshToken(): Observable<any> {
    const refresh_token = this.cookieService.get(this.TOKENS.refresh);

    return refresh_token
      ? this.http
          .post(`${this.authUrl}/token/refresh`, {
            refresh_token
          })
          .pipe(tap((authResult: AuthResultResponse) => this.setSession(authResult)))
      : throwError({
          type: MISSING_REFRESH_TOKEN_ERROR,
          message: `${this.TOKENS.refresh} is missing`
        });
  }

  public isTokenExpired(): boolean {
    return !this.cookieService.check(this.TOKENS.main);
  }

  public getToken(): string {
    return this.cookieService.get(this.TOKENS.main);
  }

  public getUserCountryCode(): Observable<string> {
    if (this.countryCode) {
      return of(this.countryCode);
    }

    return this.http
      .get<{
        country_code: string;
      }>(
        `https://ipgeolocation.abstractapi.com/v1/?api_key=${environment.ipGeoLocationKey}&fields=country_code`
      )
      .pipe(
        map(response => response.country_code),
        tap((countryCode: string) => (this.countryCode = countryCode))
      );
  }

  public deleteAccount(): Observable<any> {
    return this.http.delete(`${environment.authHost}/api/user/delete`);
  }

  private changeUserDataRequest(data: User['data']): Observable<User['data']> {
    return this.http.post<User['data']>(`${environment.authHost}/api/user/change_data`, {
      data
    });
  }

  private setSession(authResult: AuthResultResponse, confirm = false) {
    this.facebookAuthData$$.next({ code: null, state: null });

    const decodedAccessToken: JWT = this.decodeToken(authResult.token);
    if (!decodedAccessToken) {
      return;
    }

    const switchUserName = this.cookieService.get(this.TOKENS.switchedUser);
    this.switchedUser = switchUserName;

    const switchUserParams = this.switchedUser
      ? {
          impersonated: true,
          username: switchUserName
        }
      : {};

    this.user = {
      id: decodedAccessToken.id,
      username: decodedAccessToken.username,
      roles: decodedAccessToken.roles,
      isAitarget: decodedAccessToken.roles.includes('ROLE_AITARGET'),
      email: this.getEmail(decodedAccessToken.email),
      ...switchUserParams
    };

    this.user$$.next(this.user);

    this.cookieService.set(
      this.TOKENS.main,
      authResult.token,
      dayjs.unix(decodedAccessToken.exp).toDate(),
      '/',
      this.cookieDomain
    );
    if (authResult.refresh_token) {
      this.cookieService.set(
        this.TOKENS.refresh,
        authResult.refresh_token,
        dayjs().add(30, 'day').toDate(),
        '/',
        this.cookieDomain
      );
    }
  }
}
